<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  boardId: {
    type: Number,
    required: true,
  },
  column: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const selectedLabelId = ref('');

const rules = computed(() =>
  store.getters['kanban/getRoutingRulesByColumnId'](props.column.id)
);
const allLabels = computed(() => store.getters['labels/getLabels'] || []);
const routedLabelIds = computed(() =>
  rules.value.map(rule => Number(rule.label_id))
);
const availableLabels = computed(() =>
  allLabels.value
    .filter(label => !routedLabelIds.value.includes(label.id))
    .map(label => ({ value: label.id, label: label.title }))
);

const loadRules = async () => {
  try {
    await store.dispatch('kanban/getRoutingRules', {
      boardId: props.boardId,
      columnId: props.column.id,
    });
  } catch (error) {
    useAlert(t('KANBAN.ROUTING_RULES.API.ERROR_MESSAGE'));
  }
};

const open = () => {
  dialogRef.value?.open();
  selectedLabelId.value = '';
  loadRules();
};

const addRule = async () => {
  if (!selectedLabelId.value) return;
  try {
    await store.dispatch('kanban/createRoutingRule', {
      boardId: props.boardId,
      columnId: props.column.id,
      labelId: selectedLabelId.value,
    });
    selectedLabelId.value = '';
  } catch (error) {
    useAlert(t('KANBAN.ROUTING_RULES.API.ERROR_MESSAGE'));
  }
};

const removeRule = async rule => {
  try {
    await store.dispatch('kanban/deleteRoutingRule', {
      boardId: props.boardId,
      columnId: props.column.id,
      ruleId: rule.id,
    });
  } catch (error) {
    useAlert(t('KANBAN.ROUTING_RULES.API.ERROR_MESSAGE'));
  }
};

const labelTitle = labelId => {
  const matchingLabel = allLabels.value.find(
    candidate => candidate.id === Number(labelId)
  );
  return matchingLabel?.title ?? String(labelId);
};

onMounted(() => {
  if (!allLabels.value.length) {
    store.dispatch('labels/get');
  }
});

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KANBAN.ROUTING_RULES.TITLE')"
    :description="t('KANBAN.ROUTING_RULES.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="md"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.ROUTING_RULES.ADD_LABEL') }}
        </p>
        <div class="flex items-center gap-2">
          <ComboBox
            v-model="selectedLabelId"
            :options="availableLabels"
            :placeholder="t('KANBAN.ROUTING_RULES.LABEL_PLACEHOLDER')"
            :empty-state="t('KANBAN.ROUTING_RULES.NO_LABELS')"
            class="flex-1"
          />
          <Button
            variant="outline"
            icon="i-lucide-plus"
            :label="t('KANBAN.ROUTING_RULES.ADD')"
            :disabled="!selectedLabelId"
            @click="addRule"
          />
        </div>
      </div>
      <ul v-if="rules.length" class="flex flex-col gap-2">
        <li
          v-for="rule in rules"
          :key="rule.id"
          class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-n-weak bg-n-surface-1"
        >
          <span class="text-sm text-n-slate-12 truncate">
            {{ labelTitle(rule.label_id) }}
          </span>
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            class="!px-1.5"
            icon="i-lucide-trash"
            :aria-label="t('KANBAN.ROUTING_RULES.REMOVE_TITLE')"
            :title="t('KANBAN.ROUTING_RULES.REMOVE_TITLE')"
            @click="removeRule(rule)"
          />
        </li>
      </ul>
      <p
        v-else
        class="text-sm text-n-slate-10"
        data-testid="routing-rules-empty"
      >
        {{ t('KANBAN.ROUTING_RULES.EMPTY') }}
      </p>
    </div>
  </Dialog>
</template>
